import { useEffect } from "react";
import {
  Outlet,
  createFileRoute,
  notFound,
  redirect,
  useRouterState,
} from "@tanstack/react-router";
import { forgetWorkspace } from "@/lib/last-place";
import { trpc } from "@/lib/trpc";
import { createReportSearchParams } from "@/lib/report-search-params";
import {
  REPORTING_VIEW_KEYS,
  mediaPerformancePath,
  readReportingView,
  resolveReportingView,
  saveReportingView,
} from "@/lib/reporting-view";
import { viewFromPathname } from "./$orgSlug.index";
import WorkspaceDefaultActions from "@/components/analytics/WorkspaceDefaultActions";
import PageHeader from "@/components/shell/PageHeader";
import Button from "@/components/ui/Button";
import EmptyState from "@/components/ui/EmptyState";
import { Page, PageBody } from "@/components/ui/Page";

// Redirect-loop breaker. On 16 Sep 2026 a tab sat in this route's redirects
// for nine hours at eight `reportingView` requests a second. Whatever the
// trigger, a redirect to a target this route just redirected to is a loop:
// stop redirecting, report once, and let the page render as resolved.
// Every entry that settles resets the count, so only redirects with no
// successful load between them add up.
const REDIRECT_LOOP_LIMIT = 5;
let consecutiveRedirects = 0;

function isRedirectLoop(href: string) {
  consecutiveRedirects += 1;
  if (consecutiveRedirects < REDIRECT_LOOP_LIMIT) return false;
  consecutiveRedirects = 0;
  console.error(`Media Performance redirect loop stopped at ${href}`);
  return true;
}

export const Route = createFileRoute("/_authenticated/$orgSlug/media-performance")({
  beforeLoad: async ({ params, search, location, context }) => {
    const viewerOptions = trpc.organization.viewer.queryOptions();
    const previousViewer = context.queryClient.getQueryData(viewerOptions.queryKey);
    // One fresh authorized response ties the storage namespace to its configuration.
    const loaded = await context.queryClient.fetchQuery({
      ...trpc.mediaPerformanceConfiguration.reportingView.queryOptions({ orgSlug: params.orgSlug }),
      staleTime: 0,
    });
    const { viewer, organization: org, configuration } = loaded;
    if (previousViewer && previousViewer.userId !== viewer.userId) {
      await context.queryClient.cancelQueries();
      context.queryClient.clear();
    }
    context.queryClient.setQueryData(viewerOptions.queryKey, viewer);
    if (!org || !configuration) {
      forgetWorkspace(params.orgSlug);
      if (isRedirectLoop("/")) throw notFound();
      throw redirect({ to: "/", replace: true });
    }
    context.queryClient.setQueryData(
      trpc.mediaPerformanceConfiguration.load.queryOptions({ orgId: org.id }).queryKey,
      configuration,
    );
    const scope = { userId: viewer.userId, orgId: org.id };
    const raw = new URLSearchParams(location.searchStr);
    const explicit = REPORTING_VIEW_KEYS.some((key) => raw.has(key));
    const pathView = viewFromPathname(location.pathname);
    const resolved = resolveReportingView({
      search,
      explicit,
      personal: readReportingView(scope),
      section:
        pathView && pathView !== "overview"
          ? pathView
          : explicit
            ? (search.view ?? "overview")
            : undefined,
      configuration,
    });
    const { view } = resolved.search;
    const pathname = mediaPerformancePath(params.orgSlug, view);
    const canonicalSearch = { ...search, ...resolved.search };
    const query = createReportSearchParams(canonicalSearch, new Set());
    const href = `${pathname}?${query.toString()}`;
    if (
      (location.pathname.replace(/\/$/, "") !== pathname ||
        REPORTING_VIEW_KEYS.some(
          (key) => JSON.stringify(search[key]) !== JSON.stringify(resolved.search[key]),
        )) &&
      !isRedirectLoop(href)
    ) {
      throw redirect({ href, replace: true });
    }
    consecutiveRedirects = 0;
    return { reportingView: { ...resolved, scope, configuration, workspace: org } };
  },
  component: RememberedMediaPerformance,
});

function RememberedMediaPerformance() {
  const { reportingView } = Route.useRouteContext();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const idle = useRouterState({ select: (state) => state.status === "idle" });
  useEffect(() => {
    // Only committed routes write memory. Preloads and cancelled navigations
    // never overwrite the user's next clean entry with transient selections.
    if (idle && !reportingView.unavailable.length)
      saveReportingView(reportingView.scope, reportingView.search);
  }, [idle, reportingView]);
  if (reportingView.unavailable.length) {
    const availableIds = new Set(reportingView.configuration.breakdowns.map((item) => item.id));
    return (
      <Page>
        <PageHeader title="Media Performance" actions={<WorkspaceDefaultActions />} />
        <PageBody>
          <div role="alert">
            <EmptyState
              title={`The selected ${reportingView.unavailable.join(" and ")} is unavailable for this Company.`}
              action={
                <Button
                  variant="primary"
                  onClick={() =>
                    void navigate({
                      search: {
                        ...search,
                        segment: reportingView.configuration.filters.some(
                          (item) => item.id === search.segment,
                        )
                          ? search.segment
                          : "all",
                        breakdown: (search.breakdown ?? []).filter((id) => availableIds.has(id)),
                      },
                    })
                  }
                >
                  Use available selections
                </Button>
              }
            >
              The report has not been loaded.
            </EmptyState>
          </div>
        </PageBody>
      </Page>
    );
  }
  return <Outlet key={`${reportingView.scope.userId}:${reportingView.scope.orgId}`} />;
}
